import { useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { Skeleton } from '@/components/ui/Skeleton';
import { InfoTray } from '@/components/ui/InfoTray';
import { InfoIcon } from '@/components/ui/Icon';
import { TrendDelta } from '@/components/ui/TrendDelta';
import { WhyThisMattersCard } from '@/components/complaint/WhyThisMattersCard';
import {
  useChiefComplaint,
  useChiefComplaints,
  useComplaintIndicators,
  useComplaintPlan,
  useComplaintTrends,
  useIndicatorDescriptions,
  useWhyThisMatters,
} from '@/hooks/api/useDashboard';
import {
  findDomain,
  isDomainKey,
  scoreBucket,
} from '@/data/mockPerformance';
import {
  isPlanAvailable,
  type ComplaintIndicator,
  type ComplaintPlanResponse,
  type ComplaintTrends,
  type IndicatorDescriptions,
  type TrendDirection,
} from '@/api/dashboard';
import { isHiddenIndicator } from '@/data/hiddenIndicators';
import { complaintSlug, lensDomainPath } from '@/routes/paths';
import { usePeriod } from '@/state/PeriodContext';
import { useSwipeTabs } from '@/hooks/useSwipeTabs';

import { PlanSections } from '../dashboard/PlanRows';
import styles from './ComplaintDetailPage.module.css';

type Tab = 'summary' | 'indicators' | 'trends' | 'plan';
type SubSortField = 'clinical' | 'score' | 'name' | 'denom';
type SubSortDir = 'asc' | 'desc';

const TAB_ORDER: readonly Tab[] = ['summary', 'indicators', 'trends', 'plan'];

const TABS: { key: Tab; label: string }[] = [
  { key: 'summary', label: 'Summary' },
  { key: 'indicators', label: 'Indicators' },
  { key: 'trends', label: 'Trends' },
  { key: 'plan', label: 'Plan' },
];

const SUB_SORT_FIELDS: { value: SubSortField; label: string }[] = [
  // Reference-data ordinality: the order the clinical team intends. Falls
  // back to worst-score-first inside groups with no ordinality data.
  { value: 'clinical', label: 'Clinical order' },
  { value: 'score', label: 'Score' },
  { value: 'name', label: 'Name' },
  { value: 'denom', label: 'Charts (N)' },
];

/**
 * Lens complaint drill-down -- /lens/:domain/:complaint.
 *
 * Layout mirrors the v14 mockup's `#ccModal`: breadcrumbs + slim title row,
 * four tabs (Summary | Indicators | Trends | Plan), each tab self-contained.
 * Rendered as a route-based page today; PLA-251 wraps this in the bottom-sheet
 * modal pattern, at which point the inner layout is reused as-is.
 *
 * Strict real-data policy: missing or zero-denom values render as "--" so the
 * UI never lies about underlying data.
 */
export function ComplaintDetailPage() {
  const { domain: domainParam, complaint: complaintParam } = useParams<{
    domain: string;
    complaint: string;
  }>();

  const [activeTab, setActiveTab] = useState<Tab>('summary');
  // Direction of the most recent tab change ('left' = moved to a tab further
  // right, as when the finger swipes left). Drives the slide-in animation and
  // is set by both swipes and tab-bar taps so the motion is consistent.
  const [slideDir, setSlideDir] = useState<'left' | 'right' | null>(null);
  const changeTab = (next: Tab) => {
    const from = TAB_ORDER.indexOf(activeTab);
    const to = TAB_ORDER.indexOf(next);
    if (to === from) return;
    setSlideDir(to > from ? 'left' : 'right');
    setActiveTab(next);
  };
  const swipe = useSwipeTabs<Tab>({
    order: TAB_ORDER,
    active: activeTab,
    onChange: (next) => changeTab(next),
  });
  const [subSort, setSubSort] = useState<SubSortField>('clinical');
  const [subDir, setSubDir] = useState<SubSortDir>('asc');

  const validDomain = isDomainKey(domainParam) ? domainParam : null;
  const complaintId = validDomain && complaintParam ? complaintParam : undefined;

  // Global period filter (same source as Home / Lens overview). Threaded
  // into every tab, Trends included, so the monthly bars cover exactly the
  // selected date range and sum to the same composite the Summary tab shows
  // for that range. The API's resolveMonthlyWindow derives the month keys
  // from the filter bounds (partial months stay partial).
  const { periodMeta } = usePeriod();
  const filters = useMemo(() => ({ dateRange: periodMeta.api }), [periodMeta.api]);

  const summaryQuery = useChiefComplaint(complaintId, filters);
  // The detail endpoint doesn't carry the org goal, so source it from the
  // same complaint list the Lens overview renders (usually served straight
  // from the React Query cache when navigating in from Lens). This keeps the
  // goal shown here identical to the tick on the Lens bar.
  const complaintsQuery = useChiefComplaints(filters);
  const indicatorsQuery = useComplaintIndicators(complaintId, filters);
  const trendsQuery = useComplaintTrends(complaintId, filters);
  const planQuery = useComplaintPlan(complaintId, filters);
  // Standalone Why-This-Matters query. The detail response inlines this
  // field; this hook acts as a fallback so the card still renders if the
  // inline value is ever undefined / null on a successful detail response.
  const whyQuery = useWhyThisMatters(complaintId);
  // Indicator + section descriptions powering the Lens info buttons. Fetched
  // once and cached; failures are non-fatal — the tray falls back to a
  // "coming soon" hint, so the rows still render.
  const descriptionsQuery = useIndicatorDescriptions();

  const groups = useMemo(
    () => groupIndicators(indicatorsQuery.data, subSort, subDir),
    [indicatorsQuery.data, subSort, subDir],
  );

  const descIndex = useMemo(
    () => buildDescIndex(descriptionsQuery.data),
    [descriptionsQuery.data],
  );

  if (!validDomain) return <Navigate to="/lens/rsq" replace />;
  if (!complaintParam) return <Navigate to={lensDomainPath(validDomain)} replace />;

  const domain = findDomain(validDomain);
  if (!domain) return <Navigate to="/lens/rsq" replace />;

  const summary = summaryQuery.data;
  const compositeRaw = summary?.documentationBreakdown.rsq.overall;
  const composite: number | null =
    typeof compositeRaw === 'number' && compositeRaw > 0 ? compositeRaw : null;
  const compositeBucket = scoreBucket(composite);
  // chartCount is the canonical "N" from the detail endpoint -- distinct
  // chart_id over the active filters. We previously read denom off the first
  // indicator row, which is per-indicator evaluation count and inflated by
  // ~14x because the densest indicator's denom got picked.
  const charts: number | null =
    typeof summary?.chartCount === 'number' && summary.chartCount > 0
      ? summary.chartCount
      : null;
  // patientCount is null on the API until silver carries a patient id;
  // gender.male + gender.female previously summed indicator-evaluation rows
  // and reported a ~14x-inflated number. Render "--" when null.
  const patientCount =
    typeof summary?.patientCount === 'number' && summary.patientCount > 0
      ? summary.patientCount
      : null;
  // Per-complaint goal, matched from the Lens list row by id or name slug.
  // Same fallback (50) and source semantics as the Lens overview bar.
  const listRow =
    (complaintsQuery.data ?? []).find(
      (r) => r.id === complaintParam || complaintSlug(r.name) === complaintParam,
    ) ?? null;
  // Display name resolution. The Lens list carries a display-ready name
  // ("Abdominal Pain"), so prefer the matched list row. The detail endpoint
  // currently echoes the slug back as `name` (e.g. "abdominal-pain"), so only
  // trust `summary.name` when it is NOT slug-shaped; otherwise un-slug the URL
  // param so a cold deep-link (empty list cache) still reads cleanly instead of
  // rendering the raw slug. See the backend follow-up to have /chief-complaints/:id
  // return the same display name the list endpoint does.
  const friendlyName =
    listRow?.name ??
    (summary?.name && !looksLikeSlug(summary.name)
      ? summary.name
      : unslug(complaintParam));
  const goalPct = listRow?.goal ?? 50;
  const goalIsSet = listRow?.goalSource === 'vida';

  return (
    <section className={styles.page} aria-labelledby="complaint-heading">
      <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
        <Link to="/lens/rsq" className={styles.breadcrumb}>
          Lens
        </Link>
        <span className={styles.breadcrumbSeparator} aria-hidden="true">/</span>
        <Link to={lensDomainPath(domain.key)} className={styles.breadcrumb}>
          {domain.label}
        </Link>
        <span className={styles.breadcrumbSeparator} aria-hidden="true">/</span>
        <span className={styles.breadcrumbCurrent}>{friendlyName}</span>
      </nav>

      <div className={styles.titleRow}>
        <p className={styles.domainTag}>{domain.fullName}</p>
        <h1 id="complaint-heading" className={styles.title}>
          {summaryQuery.isLoading ? (
            <Skeleton variant="text" width="60%" height={24} />
          ) : (
            friendlyName
          )}
        </h1>
      </div>

      <nav
        className={styles.tabBar}
        role="tablist"
        aria-label="Complaint detail sections"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            aria-controls={`cc-tab-${t.key}`}
            id={`cc-tab-button-${t.key}`}
            className={
              activeTab === t.key ? `${styles.tab} ${styles.tabActive}` : styles.tab
            }
            onClick={() => changeTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {/* key={activeTab} remounts the panel on every tab change so the slide
          animation replays; the swipe handlers live on the panel so a
          horizontal flick anywhere in the content moves between tabs. */}
      <div
        key={activeTab}
        className={`${styles.tabPanel} ${
          slideDir === 'left'
            ? styles.tabPanelSlideLeft
            : slideDir === 'right'
              ? styles.tabPanelSlideRight
              : ''
        }`}
        role="tabpanel"
        id={`cc-tab-${activeTab}`}
        aria-labelledby={`cc-tab-button-${activeTab}`}
        {...swipe.handlers}
      >
        {activeTab === 'summary' && (
          <SummaryTab
            isLoading={summaryQuery.isLoading || indicatorsQuery.isLoading}
            isError={summaryQuery.isError || indicatorsQuery.isError}
            errorMsg={
              (summaryQuery.error as Error)?.message ??
              (indicatorsQuery.error as Error)?.message
            }
            domainLabel={domain.label}
            composite={composite}
            compositeBucket={compositeBucket}
            goalPct={goalPct}
            goalIsSet={goalIsSet}
            charts={charts}
            patientCount={patientCount}
            rank={summary?.rank ?? null}
            peerCohort={summary?.peerCount ?? null}
            periodChange={summary?.change ?? null}
            periodChangeDir={summary?.trend ?? 'flat'}
            periodDays={summary?.periodDays ?? null}
            whyThisMatters={
              summary?.whyThisMatters ?? whyQuery.data?.whyThisMatters ?? null
            }
            trends={trendsQuery.data ?? null}
          />
        )}

        {activeTab === 'indicators' && (
          <IndicatorsTab
            isLoading={indicatorsQuery.isLoading}
            isError={indicatorsQuery.isError}
            groups={groups}
            descIndex={descIndex}
            subSort={subSort}
            subDir={subDir}
            onSortChange={setSubSort}
            onDirToggle={() => setSubDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
          />
        )}

        {activeTab === 'trends' && (
          <TrendsTab
            isLoading={trendsQuery.isLoading}
            isError={trendsQuery.isError}
            data={trendsQuery.data ?? null}
            compositeBucket={compositeBucket}
            fallbackCurrent={composite}
          />
        )}

        {activeTab === 'plan' && (
          <PlanTab isLoading={planQuery.isLoading} data={planQuery.data ?? null} />
        )}
      </div>
    </section>
  );
}

/* ============================================================================
   Summary tab — score hero + WhyThisMatters + stats grid + AI insight.
   ============================================================================ */

interface SummaryTabProps {
  isLoading: boolean;
  isError: boolean;
  errorMsg?: string;
  domainLabel: string;
  composite: number | null;
  compositeBucket: ReturnType<typeof scoreBucket>;
  /** Per-complaint goal percent (org-set Vida target, or the 50 default). */
  goalPct: number;
  /** True when the goal is an organization-set target rather than the default. */
  goalIsSet: boolean;
  charts: number | null;
  patientCount: number | null;
  /** Provider's tenant-wide rank for this complaint (1 = best), or null. */
  rank: number | null;
  /** Total tenant peers ranked for this complaint, or null. */
  peerCohort: number | null;
  /**
   * Period-over-period change from the detail endpoint: this window's
   * composite minus the prior window of equal length (same complaint,
   * provider, hospital, formula). Null for open-ended windows.
   */
  periodChange: number | null;
  periodChangeDir: TrendDirection;
  /** Length of the active window in days (labels the chip). */
  periodDays: number | null;
  whyThisMatters: NonNullable<
    ReturnType<typeof useChiefComplaint>['data']
  >['whyThisMatters'];
  trends: ComplaintTrends | null;
}

function SummaryTab({
  isLoading,
  isError,
  errorMsg,
  domainLabel,
  composite,
  compositeBucket,
  goalPct,
  goalIsSet,
  charts,
  patientCount,
  rank,
  peerCohort,
  periodChange,
  periodChangeDir,
  periodDays,
  whyThisMatters,
  trends,
}: SummaryTabProps) {
  // Trends carry their own peer cohort count (per rolling window). Prefer
  // the complaint-detail rank/cohort (computed by the API over the active
  // filter window with the same formula as the summary score) and fall back
  // to the trends count when the complaint-detail value is absent.
  const peerCount = peerCohort ?? trends?.tenantPeerCount ?? null;
  // Hero chip: true period-over-period change (this window vs the prior
  // window of equal length) from the detail endpoint, so it is the same
  // composite the hero shows compared against itself one period earlier.
  // Open-ended windows (All Time) have no prior period, so the chip hides.
  const trendDelta = periodChange;
  const trendDir: TrendDirection = periodChangeDir;
  const trendSuffix = 'vs prior period';
  const trendTitle =
    periodDays != null
      ? `Compared with the ${periodDays} days before this window`
      : 'Compared with the window before this one';
  // Change tile: first half vs second half of the selected window, split by
  // calendar day (same fact table, filter window and formula as the hero),
  // not the Databricks rolling view's 180-day delta, which was a different
  // source, window and numerator and could contradict the chip.
  const windowDelta = trends?.monthlyDelta ?? null;
  const windowDir: TrendDirection =
    windowDelta == null ? 'flat' : windowDelta >= 2 ? 'up' : windowDelta <= -2 ? 'down' : 'flat';
  const windowLabel = trends?.windowLabel ?? null;
  return (
    <div className={styles.summaryTab}>
      {isError && (
        <div className={styles.errorState} role="alert">
          <strong>Couldn't load complaint detail.</strong>
          <p>{errorMsg ?? 'Kai-API request failed.'}</p>
        </div>
      )}

      <div className={styles.heroRow}>
        <div className={styles.heroScoreBlock}>
          {isLoading ? (
            <Skeleton variant="text" width={72} height={36} />
          ) : (
            <span
              className={`${styles.heroScore} ${styles[`score_${compositeBucket}`]}`}
            >
              {composite != null ? composite : '--'}
              {composite != null && <span className={styles.heroScorePct}>%</span>}
            </span>
          )}
          <span className={styles.heroScoreLabel}>Score</span>
        </div>
        <div className={styles.heroMeta}>
          <span className={styles.heroBadge}>{domainLabel}</span>
          {trendDelta != null && (
            <span className={styles.heroTrendChip} title={trendTitle}>
              <TrendDelta delta={trendDelta} direction={trendDir} unit="pts" title={trendTitle} />
              <span className={styles.heroTrendChipSuffix}>{trendSuffix}</span>
            </span>
          )}
          <span className={styles.heroMetaLine}>
            {charts != null ? `N=${charts.toLocaleString()} charts` : 'N=-- charts'}
            {patientCount != null && patientCount > 0
              ? ` · ${patientCount.toLocaleString()} patients`
              : ''}
            {peerCount != null ? ` · ${peerCount} peers` : ''}
          </span>
        </div>
      </div>

      {!isLoading && composite != null && (
        <div className={styles.goalRow} aria-label={`Composite score vs ${goalPct}% goal`}>
          <div className={styles.goalTrack} aria-hidden="true">
            <div
              className={styles.goalMarker}
              style={{ left: `${Math.min(100, Math.max(0, goalPct))}%` }}
              title={
                goalIsSet
                  ? `Organization goal ${goalPct}%`
                  : `Default goal ${goalPct}% (none set)`
              }
            />
            <div
              className={`${styles.goalFill} ${
                composite >= goalPct ? styles.fill_surpassed : styles[`fill_${compositeBucket}`]
              }`}
              style={{ width: `${composite}%` }}
            />
          </div>
          <div className={styles.goalLegend}>
            <span>0%</span>
            <span>Goal {goalPct}%</span>
            <span>100%</span>
          </div>
        </div>
      )}

      {whyThisMatters && <WhyThisMattersCard data={whyThisMatters} />}

      <div className={styles.statsGrid}>
        <Stat
          label="Charts Analyzed"
          value={
            isLoading ? '--' : charts != null ? charts.toLocaleString() : '--'
          }
        />
        <Stat
          label={
            composite != null && composite >= goalPct
              ? `Above ${goalPct}% Goal`
              : `Below ${goalPct}% Goal`
          }
          value={
            composite != null ? (
              <TrendDelta
                delta={composite - goalPct}
                direction={composite >= goalPct ? 'up' : 'down'}
                unit="%"
                tone={false}
                title={`${Math.abs(composite - goalPct)} points ${composite >= goalPct ? 'above' : 'below'} the ${goalPct}% goal`}
              />
            ) : (
              '--'
            )
          }
          bucket={composite != null ? (composite >= goalPct ? 'excellent' : 'needs') : undefined}
          hint={composite != null ? `Composite ${composite}%` : undefined}
        />
        <Stat
          label="Peer Ranking"
          value={
            rank != null && peerCount != null
              ? `${rank.toLocaleString()} of ${peerCount.toLocaleString()}`
              : peerCount != null
                ? `${peerCount.toLocaleString()} peers`
                : '--'
          }
          hint={
            rank != null && peerCount != null
              ? 'Rank among peers for this complaint'
              : peerCount != null
                ? 'Cohort size only — anchor an NPI to see rank'
                : 'Awaiting cohort data'
          }
        />
        <Stat
          label={windowLabel ? `Change, ${windowLabel}` : 'Change Over Window'}
          value={
            windowDelta != null ? (
              <TrendDelta
                delta={windowDelta}
                direction={windowDir}
                unit="pts"
                tone={false}
              />
            ) : (
              '--'
            )
          }
          bucket={
            windowDelta != null
              ? windowDir === 'up'
                ? 'excellent'
                : windowDir === 'down'
                  ? 'needs'
                  : undefined
              : undefined
          }
          hint={
            windowDelta != null
              ? 'Late half of the window vs early half (see Trends)'
              : 'Needs at least two months with charts'
          }
        />
      </div>

      <div className={styles.aiInsight} aria-label="AI insight">
        <span className={styles.aiInsightLabel}>AI Insight</span>
        <p className={styles.aiInsightBody}>
          {composite != null && charts != null
            ? `Documentation completeness sits at ${composite}% across ${charts.toLocaleString()} charts in this window. AI-driven period-over-period commentary will appear here once the delta endpoint ships.`
            : 'Awaiting enough data to draft a per-complaint summary. The AI Insight block will populate once chart volume and trend deltas are available.'}
        </p>
      </div>

      {!whyThisMatters && !isLoading && (
        <p className={styles.tabHelper}>
          No clinical context registered for this complaint yet. The Why This
          Matters card appears here automatically once content is published.
        </p>
      )}
    </div>
  );
}

interface StatProps {
  label: string;
  value: ReactNode;
  hint?: string;
  bucket?: ReturnType<typeof scoreBucket>;
}

function Stat({ label, value, hint, bucket }: StatProps) {
  const valueClass = bucket
    ? `${styles.statValue} ${styles[`score_${bucket}`]}`
    : styles.statValue;
  return (
    <div className={styles.stat}>
      <span className={valueClass}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
      {hint && <span className={styles.statHint}>{hint}</span>}
    </div>
  );
}

/* ============================================================================
   Indicators tab — collapsible categories with sub-indicator bars.
   ============================================================================ */

interface IndicatorsTabProps {
  isLoading: boolean;
  isError: boolean;
  groups: GroupedIndicators[];
  descIndex: DescIndex;
  subSort: SubSortField;
  subDir: SubSortDir;
  onSortChange: (s: SubSortField) => void;
  onDirToggle: () => void;
}

function IndicatorsTab({
  isLoading,
  isError,
  groups,
  descIndex,
  subSort,
  subDir,
  onSortChange,
  onDirToggle,
}: IndicatorsTabProps) {
  // Which info tray is open, if any. Holds the already-resolved entry so the
  // tray renders without re-looking-up.
  const [info, setInfo] = useState<ResolvedInfo | null>(null);

  return (
    <>
      {!isLoading && groups.length > 0 && (
        <div className={styles.sortBar} role="group" aria-label="Sub-indicator sort">
          <span className={styles.sortLabel}>Indicators</span>
          <div className={styles.sortControls}>
            <label className={styles.srOnlyLabel} htmlFor="sub-sort">
              Sort indicators by
            </label>
            <select
              id="sub-sort"
              className={styles.sortSelect}
              value={subSort}
              onChange={(e) => onSortChange(e.target.value as SubSortField)}
            >
              {SUB_SORT_FIELDS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={styles.dirToggle}
              onClick={onDirToggle}
              aria-label={`Sort direction: ${subDir === 'asc' ? 'ascending' : 'descending'}`}
              title={`Toggle ${subDir === 'asc' ? 'descending' : 'ascending'}`}
            >
              {subDir === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <ul className={styles.groupList}>
          {Array.from({ length: 3 }).map((_, i) => (
            <li
              key={i}
              className={`${styles.group} ${styles.groupSkeleton}`}
            >
              <Skeleton variant="text" width="40%" />
              <Skeleton variant="block" height={6} />
              <Skeleton variant="block" height={32} />
            </li>
          ))}
        </ul>
      ) : groups.length === 0 ? (
        <EmptyBlock>
          {isError
            ? 'Indicator detail unavailable.'
            : "We don't have indicator data for this complaint yet."}
        </EmptyBlock>
      ) : (
        <ul className={styles.groupList}>
          {groups.map((group, idx) => {
            const groupScore =
              group.score != null && group.score > 0 ? group.score : null;
            const groupBucket = scoreBucket(groupScore);
            return (
              <li key={group.name} className={styles.group}>
                <details
                  className={styles.groupDetails}
                  open={idx === 0}
                >
                  <summary className={styles.groupHeader}>
                    <span
                      className={`${styles.groupColorBar} ${styles[`fill_${groupBucket}`]}`}
                      aria-hidden="true"
                    />
                    <h2 className={styles.groupTitle}>
                      {group.name}
                      <button
                        type="button"
                        className={styles.infoBtn}
                        aria-label={`About the ${group.name} section`}
                        // Stop the click from toggling the <details>.
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setInfo(resolveInfo(descIndex, 'section', group.name));
                        }}
                      >
                        <InfoIcon width={13} height={13} />
                      </button>
                    </h2>
                    <span
                      className={`${styles.groupScore} ${styles[`score_${groupBucket}`]}`}
                    >
                      {groupScore != null ? `${groupScore}%` : '--'}
                    </span>
                    <span className={styles.groupChevron} aria-hidden="true">
                      ▾
                    </span>
                  </summary>
                  <ul className={styles.subList}>
                    {group.indicators.map((sub) => {
                      const subScore =
                        typeof sub.score === 'number' && sub.denom > 0
                          ? sub.score
                          : null;
                      const subBucket = scoreBucket(subScore);
                      // Real per-indicator goal (org-set in Vida), default 40.
                      const subGoal = sub.goal ?? 40;
                      const subGoalLeft = Math.min(100, Math.max(0, subGoal));
                      const subGoalSet = sub.goalSource === 'vida';
                      return (
                        <li key={sub.id} className={styles.subItem}>
                          <div className={styles.subTopRow}>
                            <span className={styles.subName}>
                              {sub.name}
                              {sub.isQualifier && (
                                <span
                                  className={styles.qualifierTag}
                                  title="Qualifier: gates other indicators and does not score itself"
                                >
                                  Qualifier
                                </span>
                              )}
                              <button
                                type="button"
                                className={styles.infoBtnSub}
                                aria-label={`About ${sub.name}`}
                                onClick={() =>
                                  setInfo(resolveIndicatorInfo(descIndex, sub))
                                }
                              >
                                <InfoIcon width={12} height={12} />
                              </button>
                            </span>
                            <span
                              className={`${styles.subScore} ${styles[`score_${subBucket}`]}`}
                            >
                              {subScore != null ? `${subScore}%` : '--'}
                            </span>
                          </div>
                          <div className={styles.subTrack} aria-hidden="true">
                            <span
                              className={styles.subGoalLabel}
                              style={{ left: `${subGoalLeft}%` }}
                              title={
                                subGoalSet
                                  ? `Organization goal ${subGoal}%`
                                  : `Default goal ${subGoal}% (none set)`
                              }
                            >
                              {subGoal}%
                            </span>
                            <span
                              className={styles.subGoalMarker}
                              style={{ left: `${subGoalLeft}%` }}
                            />
                            <div
                              className={`${styles.subFill} ${styles[`fill_${subBucket}`]}`}
                              style={{ width: `${subScore ?? 0}%` }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </details>
              </li>
            );
          })}
        </ul>
      )}

      <InfoTray
        open={info != null}
        onClose={() => setInfo(null)}
        eyebrow={info?.kind === 'section' ? 'Section' : 'Indicator'}
        title={info?.title ?? ''}
      >
        {info && info.hasContent ? (
          <>
            <p>{info.description}</p>
            {info.sourceLabel && (
              <p className={styles.infoSource}>Source: {info.sourceLabel}</p>
            )}
          </>
        ) : (
          <p className={styles.infoComingSoon}>Description coming soon.</p>
        )}
      </InfoTray>
    </>
  );
}

/* ============================================================================
   Trends tab — current + change over the selected period + history bars (when available).
   ============================================================================ */

interface TrendsTabProps {
  isLoading: boolean;
  isError: boolean;
  data: ComplaintTrends | null;
  compositeBucket: ReturnType<typeof scoreBucket>;
  fallbackCurrent: number | null;
}

function TrendsTab({
  isLoading,
  isError,
  data,
  compositeBucket,
  fallbackCurrent,
}: TrendsTabProps) {
  // `current` carries the window AVERAGE (volume-weighted across the months
  // shown in the bar chart); the API computes every stat on this tab from
  // that same window so nothing here can disagree with the bars.
  const current = data?.current ?? fallbackCurrent;
  const delta = data?.trendDelta ?? null;
  const direction = data?.trendDirection ?? 'flat';
  const min = data?.scoreMin ?? null;
  const max = data?.scoreMax ?? null;
  const monthly = data?.monthly ?? [];

  return (
    <div className={styles.trendsTab}>
      <div className={styles.trendsHeader}>
        <div className={styles.trendsHeaderBlock}>
          <span className={styles.trendsHeaderLabel}>Average</span>
          {isLoading ? (
            <Skeleton variant="text" width={64} height={28} />
          ) : (
            <span
              className={`${styles.trendsHeaderValue} ${styles[`score_${compositeBucket}`]}`}
            >
              {current != null ? `${current}%` : '--'}
            </span>
          )}
        </div>
        <div className={`${styles.trendsHeaderBlock} ${styles.trendsHeaderRight}`}>
          <span className={styles.trendsHeaderLabel}>Trend</span>
          {delta != null ? (
            <TrendDelta
              className={styles.trendsHeaderValue}
              delta={delta}
              direction={direction}
              unit="pts"
              size="lg"
            />
          ) : (
            <span className={styles.trendsHeaderValueMuted}>--</span>
          )}
        </div>
      </div>

      {/* Gate on the monthly bars, not the rolling-window view: the bars come
          from gold_provider_indicator_period_scores and are the tab's primary
          content. The windows array (v_gold_provider_period_trends) only adds
          per-window rollups, and its view has already gone dark once during a
          schema drift -- it must not be able to blank a tab whose data is
          fine. Errors render their own state instead of masquerading as
          "no data yet". */}
      {monthly.length > 0 ? (
        <>
          {/* Bar chart shows one column per month in the selected period
              (oldest -> newest). The Average, Trend, and Range all derive from this same window
              (API-side), so the numbers always agree with the bars.
              Matches the #ccTrendChart pattern in Mock/Kai-Mock-V2.html. */}
          <div className={styles.trendsChart} aria-label="Monthly score history">
            {monthly.map((m) => {
              const bucket = scoreBucket(m.score);
              const heightPct = m.score != null ? Math.max(4, m.score) : 0;
              return (
                <div key={m.ym} className={styles.trendsChartCol}>
                  <span
                    className={`${styles.trendsChartValue} ${styles[`score_${bucket}`]}`}
                  >
                    {m.score != null ? `${m.score}%` : '--'}
                  </span>
                  <div
                    className={`${styles.trendsChartBar} ${styles[`fill_${bucket}`]}`}
                    style={{ height: `${heightPct}%` }}
                    title={`${m.month} ${m.year} — ${m.charts.toLocaleString()} chart evaluations`}
                  />
                </div>
              );
            })}
          </div>
          <div className={styles.trendsMonths}>
            {monthly.map((m) => (
              <span key={m.ym} className={styles.trendsMonth}>
                {m.month}
              </span>
            ))}
          </div>

          {(min != null || max != null) && (
            <div className={styles.rangeBlock} aria-label="Score range">
              <div className={styles.rangeHeader}>
                <span className={styles.rangeHeaderLabel}>Range</span>
                <span className={styles.rangeHeaderValue}>
                  {min ?? '--'}% – {max ?? '--'}%
                </span>
              </div>
              <div className={styles.rangeTrack} aria-hidden="true">
                {min != null && max != null && (
                  <div
                    className={styles.rangeBar}
                    style={{
                      left: `${Math.max(0, min)}%`,
                      width: `${Math.max(2, Math.min(100, max) - Math.max(0, min))}%`,
                    }}
                  />
                )}
                {current != null && (
                  <div
                    className={styles.rangeMarker}
                    style={{ left: `${current}%` }}
                    title={`Average ${current}%`}
                  />
                )}
              </div>
            </div>
          )}
        </>
      ) : isError ? (
        <div className={styles.unavailableBlock}>
          <strong>Couldn't load trend data.</strong>
          <p>Something went wrong fetching trends for this complaint. Try reloading the page.</p>
        </div>
      ) : (
        <div className={styles.unavailableBlock}>
          <strong>Trend data isn't available yet.</strong>
          <p>
            {data?.reason ??
              'Rolling-window trends will appear here once data is available for this complaint.'}
          </p>
        </div>
      )}
    </div>
  );
}

/* ============================================================================
   Plan tab — complaint-scoped "View My Plan" (real gold data).

   Mirrors the Home PlanModal layout (performance narrative + strength /
   opportunity cards) but scoped to this chief complaint's indicators. The
   payload comes from GET /chief-complaints/:id/plan which scores the
   provider's indicator found-rates against org goals via PlanService.
   Reuses PlanModal.module.css so the two surfaces stay visually identical.
   ============================================================================ */

interface PlanTabProps {
  isLoading: boolean;
  data: ComplaintPlanResponse | null;
}

function PlanTab({ isLoading, data }: PlanTabProps) {
  if (isLoading) {
    return (
      <div className={styles.planTab}>
        <Skeleton variant="block" height={80} />
        <Skeleton variant="block" height={80} />
      </div>
    );
  }

  if (!data || !isPlanAvailable(data)) {
    return (
      <div className={styles.planTab}>
        <div className={styles.planOnTrack}>
          <strong>No plan for this window yet</strong>
          <p>
            {(data && !isPlanAvailable(data) ? data.reason : null) ??
              'A complaint-specific plan generates automatically once there is enough chart volume in the selected period.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.planTab}>
      <PlanSections
        summaryHtml={data.performance.summary}
        strengths={data.strengths}
        opportunities={data.opportunities}
        allAtGoalText="Every scoreable indicator on this complaint is at or above its goal for this window."
      />
    </div>
  );
}

/* ============================================================================
   Helpers
   ============================================================================ */

interface GroupedIndicators {
  name: string;
  score: number;
  indicators: ComplaintIndicator[];
}

/* Indicator / section description lookup.
 * ------------------------------------------------------------------------ */

/** Normalize a name to the server's lookup key (lowercased, trimmed). */
const normalizeDescKey = (name: string): string => String(name || '').trim().toLowerCase();

/** Pre-indexed descriptions: key -> entry, for indicators and sections. */
interface DescIndex {
  indicators: Map<string, { description: string; source: string }>;
  sections: Map<string, { description: string; source: string }>;
}

/** Resolved tray content for the currently-open info button. */
interface ResolvedInfo {
  kind: 'indicator' | 'section';
  title: string;
  description: string;
  /** True when we have curated (non-stub) copy to show. */
  hasContent: boolean;
  /** Human-readable provenance, shown only for curated content. */
  sourceLabel: string | null;
}

const SOURCE_LABELS: Record<string, string> = {
  tsg: 'The Sullivan Group',
  'paper-plane': 'Paper Plane',
};

/** Build the lookup maps once from the bulk descriptions response. */
const buildDescIndex = (data: IndicatorDescriptions | undefined): DescIndex => {
  const indicators = new Map<string, { description: string; source: string }>();
  const sections = new Map<string, { description: string; source: string }>();
  if (data) {
    for (const e of data.indicators ?? []) {
      indicators.set(e.key || normalizeDescKey(e.name), {
        description: e.description,
        source: e.source,
      });
    }
    for (const e of data.sections ?? []) {
      sections.set(e.key || normalizeDescKey(e.name), {
        description: e.description,
        source: e.source,
      });
    }
  }
  return { indicators, sections };
};

/**
 * Resolve a name to tray content. A missing entry or a `stub` source both
 * surface the "coming soon" hint (hasContent === false) rather than breaking
 * the row -- only reviewed (`tsg` / `paper-plane`) copy is shown verbatim.
 */
const resolveInfo = (
  index: DescIndex,
  kind: 'indicator' | 'section',
  name: string,
): ResolvedInfo => {
  const map = kind === 'section' ? index.sections : index.indicators;
  const hit = map.get(normalizeDescKey(name));
  const isCurated = !!hit && hit.source !== 'stub' && !!hit.description;
  return {
    kind,
    title: name,
    description: hit?.description ?? '',
    hasContent: isCurated,
    sourceLabel: isCurated ? SOURCE_LABELS[hit!.source] ?? null : null,
  };
};

/**
 * Indicator tray content: prefer the row's complaint-specific description
 * (Databricks reference tooltip, carried on the indicators payload) over the
 * generic bulk descriptions, which are not complaint-aware.
 */
const resolveIndicatorInfo = (index: DescIndex, row: ComplaintIndicator): ResolvedInfo => {
  if (row.description) {
    return {
      kind: 'indicator',
      title: row.name,
      description: row.description,
      hasContent: true,
      sourceLabel: SOURCE_LABELS[row.descriptionSource ?? ''] ?? SOURCE_LABELS.tsg ?? null,
    };
  }
  return resolveInfo(index, 'indicator', row.name);
};

const GROUP_ORDER = [
  'History',
  'Risk Factors',
  'Physical Exam',
  'MDM',
  'Vitals',
];



/**
 * Defensive dedup safety net.
 *
 * The Kai-API `/chief-complaints/:id/indicators` route rolls duplicate rows
 * up to the (indicator_id, scope) grain before responding, so under normal
 * operation this function is a no-op. We keep it because (a) it costs almost
 * nothing on already-unique input and (b) if the API ever regresses the dedup
 * (e.g. a future GROUP BY change that re-introduces chart_section as a
 * separate dimension), the UI won't throw React "duplicate key" warnings or
 * render the same indicator twice -- and the console.warn below will tell us
 * the API is misbehaving before the UI does.
 *
 * Counts are summed; score is recomputed from the merged denom/found so the
 * surfaced row stays faithful to the underlying data.
 */
const dedupIndicatorsById = (
  rows: ComplaintIndicator[],
  scope: string,
): ComplaintIndicator[] => {
  const byId = new Map<string, ComplaintIndicator>();
  let collisions = 0;
  for (const row of rows) {
    const key = String(row.id);
    const existing = byId.get(key);
    if (!existing) {
      byId.set(key, row);
      continue;
    }
    collisions += 1;
    const denom = (existing.denom ?? 0) + (row.denom ?? 0);
    const found = (existing.found ?? 0) + (row.found ?? 0);
    const raw = denom > 0 ? Math.round((found / denom) * 100) : 0;
    byId.set(key, {
      ...existing,
      denom,
      found,
      // Preserve the API's inversion convention: Vitals rows carry
      // compliance % (100 - found rate), so the recomputed merge score
      // must be inverted the same way or a merge silently flips it back.
      score: existing.inverted && denom > 0 ? 100 - raw : raw,
    });
  }
  if (collisions > 0 && typeof console !== 'undefined') {
    console.warn(`[lens] dedupIndicatorsById: ${collisions} duplicate rows merged for scope "${scope}".`);
  }
  return Array.from(byId.values());
};

/**
 * Group indicator rows into the History / Risk Factors / Physical Exam /
 * MDM / Vitals buckets the Lens detail page renders. Falls back to "Other"
 * for anything the API hands back without a matching domain label.
 * Within each group, sort by `field` and `dir`.
 */
const groupIndicators = (
  rows: ComplaintIndicator[] | undefined,
  field: SubSortField,
  dir: SubSortDir,
): GroupedIndicators[] => {
  if (!rows || rows.length === 0) return [];
  const byGroup = new Map<string, ComplaintIndicator[]>();
  for (const row of rows) {
    const groupName = GROUP_ORDER.includes(row.scope) ? row.scope : 'Other';
    const bucket = byGroup.get(groupName) ?? [];
    bucket.push(row);
    byGroup.set(groupName, bucket);
  }
  const factor = dir === 'asc' ? 1 : -1;
  const sortRows = (arr: ComplaintIndicator[]) => {
    const out = arr.slice();
    switch (field) {
      case 'clinical': {
        // Reference-data ordinality (the clinical team's intended order).
        // Groups with no ordinality data fall back to worst-score-first so
        // the sort never appears arbitrary.
        const hasOrdinality = out.some((r) => r.ordinality != null);
        if (hasOrdinality) {
          out.sort(
            (a, b) =>
              factor *
              ((a.ordinality ?? Number.MAX_SAFE_INTEGER) -
                (b.ordinality ?? Number.MAX_SAFE_INTEGER)),
          );
        } else {
          out.sort((a, b) => (a.score ?? 0) - (b.score ?? 0));
        }
        break;
      }
      case 'name':
        out.sort((a, b) => factor * a.name.localeCompare(b.name));
        break;
      case 'score':
        out.sort((a, b) => factor * ((a.score ?? 0) - (b.score ?? 0)));
        break;
      case 'denom':
        out.sort((a, b) => factor * ((a.denom ?? 0) - (b.denom ?? 0)));
        break;
    }
    return out;
  };
  const order = [...GROUP_ORDER, 'Other'];
  return order
    .filter((name) => byGroup.has(name))
    .map((name) => {
      const indicators = dedupIndicatorsById(sortRows(byGroup.get(name) ?? []), name);
      // Qualifiers gate other indicators and never score themselves, so
      // their structural 0% is excluded from the group aggregate.
      const scoreable = indicators.filter((r) => !r.isQualifier);
      const totalDenom = scoreable.reduce((a, r) => a + (r.denom ?? 0), 0);
      // Credit each row on the compliance scale before aggregating. Inverted
      // rows (abnormality detection, low found = good) credit denom - found;
      // positive-behavior rows such as "Vital Signs Review" credit found.
      // This lets a Vitals group mix both kinds without a blanket inversion
      // flipping the positive rows. Older API responses without the
      // `inverted` flag treat every Vitals row as inverted, as before.
      const totalCredit = scoreable.reduce((a, r) => {
        const denom = r.denom ?? 0;
        const found = r.found ?? 0;
        const inverted = r.inverted ?? name === 'Vitals';
        return a + (inverted ? denom - found : found);
      }, 0);
      const score = totalDenom > 0 ? Math.round((totalCredit / totalDenom) * 100) : 0;
      // Display-only: the group score above already includes every row, the
      // same as the API's composite. Risk-factor Positive / Negative
      // components are dropped from the visible list only.
      const visible = indicators.filter((r) => !isHiddenIndicator(r.name, r.id));
      return { name, score, indicators: visible };
    });
};

/**
 * True when a string is slug-shaped: lowercase alphanumeric words joined by
 * hyphens, with no spaces (e.g. "abdominal-pain"). Used to detect when the
 * detail endpoint has echoed the URL slug back as a `name` instead of a
 * display label, so we can fall back to un-slugging rather than showing it raw.
 */
const looksLikeSlug = (value: string): boolean =>
  /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value.trim());

/** Slug -> Title Case (e.g. "chest-pain" -> "Chest Pain"). */
const unslug = (slug: string): string =>
  slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/** Standard empty-state block used in the Indicators / Trends / Plan tabs. */
const EmptyBlock = ({ children }: { children: ReactNode }) => (
  <div className={styles.emptyBlock} role="status">
    {children}
  </div>
);
